import React, { createContext, FC, useContext, useEffect, useState } from 'react';
import queryString from 'query-string-for-all';
import { useDispatch, useSelector } from 'react-redux';
import 'intl';
import 'intl/locale-data/jsonp/en.js';
import { HubConnection } from '@microsoft/signalr/dist/esm/HubConnection';
import loadjs from 'loadjs';

import { IState } from './reducers/index';
import { IConfigReducer } from './reducers/configReducer';
import { IContextReducer } from './reducers/contextReducer';
import { setGPlacesLoaded } from './actions/contextActions';
import { EMsalErrorCode } from './tools/authTools';
import { GlobalLoader } from './components/GlobalLoader';
import { ShadowAuth } from './components/Auth/ShadowAuth';
import IntlProviderComponent from './IntlProvider';
import { SidebarTocProvider } from './components/Soge/SidebarTocContext/SidebarTocContext';
import { CustomRouter } from './tools/customRouter';
import { ClusterContextProvider } from './components/Routes/Workspace/Cluster/ClusterContext';
import { ActivityContextProvider } from './components/Routes/Workspace/Cluster/ActivityContext';
import { getApplicationConfig } from './actions/configActions';
import { DatatableContextProvider } from './components/Common/ColoredDatagrid/DatatableContext';
import { disposeSignalRConnection, ensureSignalRConnection } from './tools/signalRConnectionManager';
import { useVersionCheck } from './tools/useVersionCheck';
import { UpdateNotificationBanner } from './components/Common/UpdateNotificationBanner';
import { getPlatformConfiguration, getPremiumConfiguration } from './actions/commonActions';
import { selectSignalREnabled, selectTrackingEnabled } from './tools/platformConfiguration';
import { stopAppInsightsTracking, syncAppInsightsTracking } from './tools/appInsightsTracking';

type GetApplicationConfig = ReturnType<typeof getApplicationConfig>;
type GetPlatformConfiguration = ReturnType<typeof getPlatformConfiguration>;
type GetPremiumConfiguration = ReturnType<typeof getPremiumConfiguration>;
type SetGPlacesLoaded = ReturnType<typeof setGPlacesLoaded>;

interface IConnectionContextProps {
    connection: HubConnection | null;
    setConnection: (connection: HubConnection | null) => void;
}

export const ConnectionContext = createContext<IConnectionContextProps | undefined>(undefined);

export const useConnection = (): IConnectionContextProps | undefined => useContext(ConnectionContext);

const App: FC = () => {
    const dispatch = useDispatch();
    const { isLoggedIn, token, appConfig, appConfigLoaded, googlePlacesLoaded, trackingEnabled, signalREnabled, platformConfigurationStatus } = useSelector<IState, IContextReducer & IConfigReducer & { trackingEnabled: boolean; signalREnabled: boolean }>(state => ({
        ...state.context,
        ...state.config,
        trackingEnabled: selectTrackingEnabled(state),
        signalREnabled: selectSignalREnabled(state)
    }));

    const [errorMessage, setErrorMessage] = useState<string>('');
    const [loaded, setLoaded] = useState<boolean>(false);
    const [connection, setConnection] = useState<HubConnection | null>(null);
    const updateAvailable = useVersionCheck(loaded && appConfigLoaded);

    const { PasswordResetRequested, PasswordResetCancelled } = EMsalErrorCode;

    useEffect(() => {
        setLoaded(false);
        const isError = window.location.href.match('#error');
        const errorParams = queryString.parse(location.href.split('#')[1]);
        const _errorMessage = errorParams && errorParams.error_description as string;
        if (isLoggedIn && !googlePlacesLoaded) {
            loadjs('https://maps.googleapis.com/maps/api/js?key=AIzaSyBCl8uUA0bTn1sn6QEn-OvQLkLYpcoITOA&libraries=places&callback=Function.prototype', () => {
                dispatch<SetGPlacesLoaded>(setGPlacesLoaded());
            });
        }

        dispatch<GetApplicationConfig>(getApplicationConfig()).then(async (config) => {
            const authenticatedConfigRequests = (isLoggedIn && token)
                ? [
                    dispatch<GetPlatformConfiguration>(getPlatformConfiguration()),
                    dispatch<GetPremiumConfiguration>(getPremiumConfiguration())
                ]
                : [];
            await Promise.allSettled(authenticatedConfigRequests);
            setLoaded(true);
            if (isError && !errorMessage.match(PasswordResetRequested) && !errorMessage.match(PasswordResetCancelled)) {
                if (config?.applicationConfig?.b2cResetPasswordUri) {
                    window.location.href = config?.applicationConfig?.b2cResetPasswordUri;
                    setLoaded(true);
                } else {
                    setErrorMessage(_errorMessage);
                    setLoaded(true);
                }
            }
        });
    }, [isLoggedIn, googlePlacesLoaded]);

    useEffect(() => {
        if (!isLoggedIn) {
            stopAppInsightsTracking();
            return;
        }

        syncAppInsightsTracking({
            trackingEnabled,
            platformConfigurationLoaded: platformConfigurationStatus === 'loaded',
            instrumentationKey: appConfig.appInsights?.instrumentationKey
        });
    }, [isLoggedIn, appConfig.appInsights?.instrumentationKey, trackingEnabled, platformConfigurationStatus]);

    useEffect(() => {
        if (!isLoggedIn || !token || !signalREnabled || !appConfig.signalRUri) {
            void disposeSignalRConnection(dispatch);
            setConnection(null);
            return;
        }
        let isMounted = true;
        ensureSignalRConnection(dispatch, appConfig.signalRUri, token)
            .then(hub => {
                if (isMounted && hub) {
                    setConnection(hub);
                }
            })
            .catch(() => {
                if (isMounted) {
                    setConnection(null);
                }
            });
        return () => {
            isMounted = false;
        };
    }, [dispatch, isLoggedIn, token, appConfig.signalRUri, signalREnabled]);

    if (loaded && errorMessage) {
        return (
            <div>
                {errorMessage}
            </div>
        );
    }

    if (loaded && appConfigLoaded) {
        return (
                <IntlProviderComponent>
                    {updateAvailable && <UpdateNotificationBanner />}
                    <ConnectionContext.Provider value={{ connection, setConnection }}>
                        <ShadowAuth>
                            <SidebarTocProvider>
                                <ClusterContextProvider> {/** Move this context inside workspace after globalLayout refactoring */}
                                    <ActivityContextProvider>
                                        <DatatableContextProvider>
                                            <CustomRouter />
                                        </DatatableContextProvider>
                                    </ActivityContextProvider>
                                </ClusterContextProvider>
                            </SidebarTocProvider>
                        </ShadowAuth>
                    </ConnectionContext.Provider>
                </IntlProviderComponent>
        );
    }

    return (
        <GlobalLoader isLoading />
    );
};

export default App;